import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Minus, Search, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { useAuth } from '../auth/KeycloakContext';

interface Tenant {
  id: string;
  subdomain: string;
  name: string;
  contact_email: string | null;
  contact_phone: string | null;
  max_users: number;
  max_channels: number;
  mumble_server_id: string | null;
  mumble_server_name: string | null;
  is_active: boolean;
  created_at: string;
  user_count: number;
  channel_count: number;
}

export default function Tenants() {
  const navigate = useNavigate();
  const { keycloak } = useAuth();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (keycloak?.token) {
      loadData();
    }
  }, [keycloak?.token]);

  const loadData = async () => {
    setLoading(true);
    const res = await fetch('/api/tenants', {
      headers: { 'Authorization': `Bearer ${keycloak?.token}` }
    });
    setTenants(await res.json());
    setLoading(false);
  };

  // Filter tenants by search
  const filteredTenants = tenants.filter((tenant) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      tenant.name.toLowerCase().includes(searchLower) ||
      tenant.subdomain.toLowerCase().includes(searchLower) ||
      (tenant.contact_email || '').toLowerCase().includes(searchLower)
    );
  });

  // Pagination calculations
  const totalTenants = filteredTenants.length;
  const totalPages = Math.ceil(totalTenants / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentTenants = filteredTenants.slice(startIndex, endIndex);

  // Reset to page 1 when page size or search changes
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(currentTenants.map(t => t.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectTenant = (tenantId: string, checked: boolean) => {
    const newSet = new Set(selectedIds);
    if (checked) {
      newSet.add(tenantId);
    } else {
      newSet.delete(tenantId);
    }
    setSelectedIds(newSet);
  };

  const handleDeleteTenants = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`Möchten Sie ${selectedIds.size} Einrichtung(en) wirklich löschen?`)) return;

    setDeleting(true);
    for (const id of Array.from(selectedIds)) {
      await fetch(`/api/tenants/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${keycloak?.token}` }
      });
    }
    setSelectedIds(new Set());
    await loadData();
    setDeleting(false);
  };

  const allSelected = currentTenants.length > 0 && currentTenants.every(t => selectedIds.has(t.id));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-sayses-accent border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-slate-800">Einrichtungen</h1>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {/* Toolbar */}
        <div className="p-4 border-b border-slate-200 flex items-center gap-4">
          <button
            onClick={() => navigate('/tenants/new')}
            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
            title="Einrichtung hinzufügen"
          >
            <Plus size={20} />
          </button>
          <button
            onClick={handleDeleteTenants}
            disabled={selectedIds.size === 0 || deleting}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Ausgewählte Einrichtungen löschen"
          >
            <Minus size={20} />
          </button>
          <div className="flex-1 flex items-center gap-2">
            <div className="relative flex-1 max-w-md">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Suchen..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sayses-accent focus:border-sayses-accent outline-none"
              />
            </div>
            <span className="text-sm text-slate-600">Alle auswählen</span>
            <input
              type="checkbox"
              checked={allSelected}
              onChange={(e) => handleSelectAll(e.target.checked)}
              className="w-4 h-4 text-sayses-accent border-slate-300 rounded focus:ring-sayses-accent"
            />
          </div>
        </div>

        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="w-12 px-6 py-3"></th>
              <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Name</th>
              <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Subdomain</th>
              <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Server</th>
              <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {currentTenants.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center text-slate-500">
                  Keine Einrichtungen vorhanden.
                </td>
              </tr>
            ) : (
              currentTenants.map((tenant) => (
                <tr key={tenant.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(tenant.id)}
                      onChange={(e) => handleSelectTenant(tenant.id, e.target.checked)}
                      className="w-4 h-4 text-sayses-accent border-slate-300 rounded focus:ring-sayses-accent"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => navigate(`/tenants/${tenant.id}/edit`)}
                      className="text-sayses-accent hover:text-sayses-accent-hover underline"
                    >
                      {tenant.name}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <a
                      href={`https://${tenant.subdomain}.sayseswork.com`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sayses-accent hover:underline flex items-center gap-1"
                    >
                      {tenant.subdomain}
                      <ExternalLink size={14} />
                    </a>
                  </td>
                  <td className="px-6 py-4 text-slate-600">
                    {tenant.mumble_server_name || '-'}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`px-2 py-1 rounded-full text-xs ${
                        tenant.is_active
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {tenant.is_active ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {totalTenants > 0 && (
          <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">Einträge pro Seite:</span>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="border border-slate-300 rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-sayses-accent focus:border-sayses-accent outline-none"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </select>
            </div>

            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-600">
                {startIndex + 1}-{Math.min(endIndex, totalTenants)} von {totalTenants}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-1 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={20} className="text-slate-600" />
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-1 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={20} className="text-slate-600" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
